import React, {useCallback, useState} from 'react';
import {Typography, Button, IconButton, Switch, Tooltip} from '@material-ui/core';
import {
  VisibilityOff,
  Visibility,
  ExpandLess,
  ExpandMore,
  Info,
  ArrowBack,
  ArrowForward,
} from '@material-ui/icons';
import {StyledTableCell} from './StyledTableCell';

const MetricHeader = React.memo(
  ({
    metric,
    metricsConfig,
    enabledMetric,
    toggleMetric,
    handleSortClick,
    sortByKey,
    sortOrder,
    hideSeparateGraphs,
    setHideSeparateGraphs,
    theme,
    type,
    isVisible, // Used for carousel filtering (handled in parent)
    allMetrics,
    onMoveColumn,
  }) => {
    const [isHovered, setIsHovered] = useState(false);
    const config = metricsConfig.perSource[metric];
    const isMetricActive = enabledMetric.includes(config.dataField);

    const handleSortClickWithStopPropagation = useCallback(
      e => {
        e.stopPropagation();
        handleSortClick(config.dataField);
      },
      [handleSortClick, config.dataField]
    );

    const handleHideSeparateGraphsChange = useCallback(
      e => {
        e.stopPropagation();
        setHideSeparateGraphs(!e.target.checked);
      },
      [setHideSeparateGraphs]
    );

    const handleMoveLeft = useCallback(
      e => {
        e.stopPropagation();
        if (onMoveColumn) onMoveColumn(metric, -1);
      },
      [metric, onMoveColumn]
    );

    const handleMoveRight = useCallback(
      e => {
        e.stopPropagation();
        if (onMoveColumn) onMoveColumn(metric, 1);
      },
      [metric, onMoveColumn]
    );

    const currentIndex = allMetrics ? allMetrics.indexOf(metric) : -1;
    const columnCarousel = type === 'analytics' || type === 'demand';
    const canMoveLeft = columnCarousel && allMetrics && currentIndex > 0;
    const canMoveRight =
      columnCarousel && allMetrics && currentIndex >= 0 && currentIndex < allMetrics.length - 1;

    return (
      <StyledTableCell
        key={metric}
        className="metricHeaderCell"
        align="left"
        style={{
          background: isMetricActive
            ? theme.palette.graphColors.themeDarkGray
            : theme.palette.background.paper,
          minWidth: type === 'analytics' || type === 'demand' ? 250 : undefined,
          position: 'relative',
        }}
        onClick={() => toggleMetric(config.dataField)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Typography variant={'caption'} style={{display: 'block'}}>
          <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
            <div style={{display: 'flex', alignItems: 'center'}}>
              <Tooltip title={isMetricActive ? `` : `Show ${config.displayName}`}>
                <Button
                  onClick={() => toggleMetric(config.dataField)}
                  endIcon={!isMetricActive ? <VisibilityOff /> : <Visibility />}
                  style={{textTransform: 'none', fontWeight: 'bold'}}
                >
                  {config.displayName}
                </Button>
              </Tooltip>
              {config.tooltip && (
                <Tooltip title={<span style={{whiteSpace: 'pre-line'}}>{config.tooltip}</span>}>
                  <IconButton size="small">
                    <Info />
                  </IconButton>
                </Tooltip>
              )}
            </div>
            <div style={{display: 'flex', alignItems: 'center'}}>
              {!config.cumulative && (
                <Tooltip title={'Separate by source'}>
                  <span style={{display: 'inline-flex', alignItems: 'center'}}>
                    <Switch
                      disabled={!isMetricActive}
                      color={'primary'}
                      checked={!hideSeparateGraphs}
                      onClick={e => e.stopPropagation()}
                      onChange={handleHideSeparateGraphsChange}
                    />
                  </span>
                </Tooltip>
              )}
              <Tooltip
                title={`Sort data by ${sortOrder !== 'asc' ? 'ascending' : 'descending'} ${
                  config.displayName
                }`}
              >
                <IconButton size="small" onClick={handleSortClickWithStopPropagation}>
                  {sortByKey === config.dataField ? (
                    sortOrder === 'asc' ? (
                      <ExpandLess />
                    ) : (
                      <ExpandMore />
                    )
                  ) : (
                    <ExpandMore style={{opacity: 0.3}} />
                  )}
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </Typography>
        {(type === 'analytics' || type === 'demand') && onMoveColumn && (
          <>
            {isHovered && canMoveLeft && (
              <Tooltip title={`Move ${config.displayName} column to the left`}>
                <IconButton
                  size="small"
                  disabled={!canMoveLeft}
                  onClick={handleMoveLeft}
                  color="primary"
                  style={{
                    position: 'absolute',
                    left: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                  }}
                >
                  <ArrowBack fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {isHovered && canMoveRight && (
              <Tooltip title={`Move ${config.displayName} column to the right`}>
                <IconButton
                  size="small"
                  disabled={!canMoveRight}
                  onClick={handleMoveRight}
                  color="primary"
                  style={{
                    position: 'absolute',
                    right: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                  }}
                >
                  <ArrowForward fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </>
        )}
      </StyledTableCell>
    );
  }
);

export default MetricHeader;
